import React, { useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

import { useAccomodationDiagram } from '../AccomodationDiagramContext';
import { ColorPicker } from '../../../../components/ColorPicker';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { baseEventWidth } from '../constants';

function hasConnections(nodeId, edges) {
  return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
}

export default function HouseContextMenu({
  id,
  top,
  left,
  right,
  bottom,
  updateNode,
  onPaneClick,
  removeNode,
  setOpenEditHouseDialog,
  ...props
}) {
  const { getNode } = useReactFlow();
  const node = getNode(id);
  const { nodes, edges, setEdges, setShowComments, addNodesF, setRecalculateHousePositions, updateNodeF, setShowChangeAgeRangeDialog } = useAccomodationDiagram();
  const [selectedColor, setSelectedColor] = useState('#ffffff'); // Domyślny kolor
  const isConnected = hasConnections(id, edges);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  const handleColorChange = color => {
    const newColor = color.hex; // Nowy kolor z inputu
    setSelectedColor(newColor); // Ustaw nowy kolor

    // Wywołaj funkcję updateNode po wyborze koloru
    const node = getNode(id);
    if (node) {
      const updatedNode = {
        ...node,
        style: {
          ...node.style,
          backgroundColor: newColor // Zastosuj nowy kolor
        }
      };
      updateNode(updatedNode); // Zaktualizuj węzeł
      // onPaneClick();
    }
  };

  const handleDeleteHouse = () => {
    // Można usunąć tylko wtedy, gdy żadne łóżko nie jest zakwaterowane
    const checkIfHouseHasOrders = nodes.filter(n => n.data.houseId == id && n.data.order);
    if (checkIfHouseHasOrders.length > 0) {
      toast.error('Aby usunąć domek musisz najpierw wykwaterować z niego wszystkich uczestników');
      onPaneClick();

      return;
    }
    removeNode(id);
    onPaneClick();
  };

  const handleDeleteEdges = () => {
    // Pobieramy połączenie studio - edge
    const edge = edges.filter(edge => edge.source == id || edge.target == id);
    // Edge source i target to id domków, pobieramy powiązane z tymi domkami imprezy
    const events = nodes.filter(n => n.parentId == edge[0].source || n.parentId == edge[0].target);
    // Mapowanie do listy id imprez
    const event_ids = events.map(obj => obj.id);
    // Pobieramy wszystkie łóżka z tych imprez
    const beds = nodes.filter(n => event_ids.includes(n.parentId));
    const relatedGroupHouseIds = {};
    // Tworzymy obiekt klucz - ID powiązanej grupy zakwaterowanego łóżka, wartość - lista ID domków tych łóżek
    beds.map(bed => {
      if (bed.data.order?.relatedGroup) {
        if (bed.data.order.relatedGroup.id in relatedGroupHouseIds) {
          relatedGroupHouseIds[bed.data.order.relatedGroup.id].push(bed.data.houseId);
        } else {
          relatedGroupHouseIds[bed.data.order.relatedGroup.id] = [bed.data.houseId];
        }
      }
    });
    // Sprawdzamy czy lista [target, source] zawiera się w otrzymanych listach domków.
    // Jeśli zwraca true, to znaczy że dana powiązana grupa znajduje się w dwóch domkach - blokujemy możliwość usunięcia połączenia studio
    let hasError = false;
    for (const key in relatedGroupHouseIds) {
      const containsAll = [edge[0].target, edge[0].source].every(element => relatedGroupHouseIds[key].includes(element));
      if (containsAll) {
        toast.error('Nie można usunąć połączenia studio, jeśli conajmniej jedna powiązana grupa znajduje się w obydwu połączonych domkach');
        hasError = true;
        break;
      }
    }
    if (!hasError) {
      setEdges(prevEdges => prevEdges.filter(edge => edge.source !== id && edge.target !== id));
    }
    onPaneClick();
  };

  const prepareEmptyEventNode = (ageRangeNode, houseNode) => {
    const eventHeight = houseNode.style.height - 28;
    const node = {
      id: `${houseNode.id}E`,
      position: {
        x: (houseNode.style.width - baseEventWidth) / 2,
        y: (houseNode.style.height - eventHeight) / 1.5
      },
      data: { houseId: houseNode.id,
        ageRangeNodeDataId: ageRangeNode.id,
        ageRangeId: ageRangeNode.data.id,
        eventId: '0000',
        eventName: 'Nie przypisano' },
      style: { width: baseEventWidth, height: eventHeight },
      parentId: houseNode.id,
      extent: 'parent',
      className: 'light',
      type: 'EventNode',
      draggable: false
    };

    return node;
  };

  const handleAddNewBed = () => {
    const houseNode = getNode(id);
    // const ageRangeNode = getNode(houseNode.data.ageRangeId);
    const ageRangeNode = nodes.find(n => n.type == 'AgeRangeNode' && n.data.id == houseNode.data.ageRangeId);
    const baseWidth = 180;
    const baseHeight = 30;
    let i = 0;
    let eventNode = nodes.find(n => n.id === `${houseNode.id}E`);
    if (!eventNode) {
      // Nie ma node'a z nieprzypisaną imprezą, tworzymy na nowo
      eventNode = prepareEmptyEventNode(ageRangeNode, houseNode);
      addNodesF([eventNode]);
    } else {
      // Znależliśmy node z pustą imprezą, szukamy łóżka z najwyższym kluczem
      nodes.filter(n => n.parentId == eventNode.id).map(n => {
        if (n.data.slot >= i) {
          i = n.data.slot + 1;
        }
      });
    }
    const bedNode = {
      id: `${eventNode.id}${uuidv4()}`,
      position: {
        x: (eventNode.style.width - baseWidth) / 2,
        y: (eventNode.style.height) - 44
      },
      data: { houseId: houseNode.id,
        ageRangeNodeDataId: ageRangeNode.id,
        ageRangeId: ageRangeNode.data.id,
        order: null,
        slot: i + 1 },
      style: { width: baseWidth, height: baseHeight },
      parentId: eventNode.id,
      extent: 'parent',
      className: 'light',
      type: 'BedNode',
      draggable: false
    };
    addNodesF([bedNode]);
    setRecalculateHousePositions([houseNode.id]);
    onPaneClick();
  };

  const handleFixedNode = draggable => {
    const updatedNode = node;
    if (draggable) {
      const { draggable, ...restNode } = updatedNode;
      updateNodeF(restNode);
    } else {
      updatedNode.draggable = draggable;
      updateNodeF(updatedNode);
    }
    onPaneClick();
  };

  const canMoveToAnotherAgeRange = React.useCallback(() => {
    // Sprawdzamy czy są zakwaterowane miejsca w tym domku
    const accommodatedBedNodes = nodes.filter(n => n.type === 'BedNode' && n.data.order && n.data.houseId == node.id);
    if (accommodatedBedNodes.length > 0) {
      return true;
    }
    // Sprawdzamy czy do domku nie zostały już przypisane inne grupy imprezowe od bazowej
    const eventGroupNodes = nodes.filter(n => n.data.houseId == node.id && n.type === 'EventNode' && n.data.eventId !== '0000');
    if (eventGroupNodes.length > 0) {
      return true;
    }

    return false;
  }, [nodes]);


  return (
    <div
      style={{ top, left, right, bottom }}
      className="context-menu"
      {...props}
    >
      <ColorPicker color={selectedColor} handleColorChange={handleColorChange} />
      <button onClick={() => setOpenEditHouseDialog(true)}>
        Edytuj dane
      </button>
      <button onClick={handleAddNewBed}>
        Dodaj miejsce
      </button>
      <button onClick={handleDeleteEdges} disabled={!isConnected}>
        Usuń połączenie studio
      </button>
      <button onClick={() => setShowConfirmDialog(true)}>
        Usuń domek
      </button>
      {node.draggable === false
        ? <button onClick={() => handleFixedNode(true)}>Odklej</button>
        : <button onClick={() => handleFixedNode(false)}>Przyklej</button>
      }
      <button onClick={() => setShowComments(true)}>Pokaż komentarze</button>
      <button disabled={canMoveToAnotherAgeRange()} onClick={() => setShowChangeAgeRangeDialog(true)}>
        Przenieś do innej strefy
      </button>
      <ConfirmDialog
        title="Usunąć domek?"
        open={showConfirmDialog}
        setOpen={setShowConfirmDialog}
        onConfirm={handleDeleteHouse}
      >
        Czy na pewno chcesz usunąć ten domek?
      </ConfirmDialog>
    </div>
  );
}
